/**
 * @module components/DataLabel
 * @description 数据标签包裹组件 — 开关 + 显示形式 + 标签内容
 *
 * 状态管理模式：
 *   读: useSettingState('showDataLabel' / 'displayForm')、useSettingObjectState('labelContent')
 *   写: setValue / 开关联动 labelContent 时 usePatchDraft
 *
 * 子项显隐：开关关闭时隐藏 displayForm + labelContent
 */

import React, { useCallback, useMemo } from 'react';
import { Switch, Select } from 'tea-component';
import { useIntl } from 'umi';
import LabelContent from './LabelContent';
import ConfigCard from '@/pages/datalab/components/ConfigCard';
import {
  usePatchDraft,
  useSettingObjectState,
  useSettingState,
} from '../../context';
import type { TChartSettingsConfigLayer } from '../../types';
import { useQuerySelector } from '../../hooks/deps';
import { toChartTypeEnum } from '../../utils/toChartTypeEnum';
import { ChartType } from '@dbl/core/chart';
// import './index.scss';

interface DataLabelProps {
  visibleItems?: Record<string, boolean>;
}

const DataLabel: React.FC<DataLabelProps> = () => {
  const { formatMessage } = useIntl();
  const patchDraft = usePatchDraft();
  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const chartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );

  const { value: showDataLabel, setValue: setShowDataLabel } =
    useSettingState('showDataLabel');
  const { value: displayForm, setValue: setDisplayForm } =
    useSettingState('displayForm');
  const { value: labelContent } = useSettingObjectState('labelContent');

  const displayFormOptions = useMemo(
    () => [
      { value: 'adaptive', text: formatMessage({ id: 'dbl_chart_adaptive' }) },
      { value: 'all', text: formatMessage({ id: 'dbl_chart_all' }) },
    ],
    [formatMessage],
  );

  const handleToggle = useCallback(
    (on: boolean) => {
      if (!on) {
        setShowDataLabel(false);
        return;
      }
      const patch: Partial<TChartSettingsConfigLayer> = { showDataLabel: true };
      // 打开开关时，若 labelContent 为空则给默认值（对齐旧代码 hooks.ts:473）
      // 百分比类图表默认勾选 percent，与 PercentStackBar 一致
      if (!labelContent || labelContent.length === 0) {
        const isPercentAxisChart =
          chartType === ChartType.PercentStackBar
          || chartType === ChartType.PercentLine;
        patch.labelContent = isPercentAxisChart ? ['percent'] : ['metric'];
      }
      patchDraft(patch);
    },
    [patchDraft, labelContent, chartType, setShowDataLabel],
  );

  const handleDisplayFormChange = useCallback(
    (value: string) => {
      setDisplayForm(value);
    },
    [setDisplayForm],
  );

  return (
    <ConfigCard
      className="dbl-data-label"
      title={<span>{formatMessage({ id: 'dbl_chart_show_data_label' })}</span>}
      operation={<Switch value={showDataLabel} onChange={handleToggle} />}
    >
      {showDataLabel && (
        <>
          <div className="dbl-chart-setting-row">
            <span className="dbl-lable-gray-font">{formatMessage({ id: 'dbl_chart_display_form' })}</span>
            <Select
              appearance="button"
              value={displayForm}
              options={displayFormOptions}
              onChange={handleDisplayFormChange}
            />
          </div>
          <div className="dbl-chart-setting-row">
            <span className="dbl-lable-gray-font">{formatMessage({ id: 'dbl_chart_label_content' })}</span>
            <LabelContent />
          </div>
        </>
      )}
    </ConfigCard>
  );
};

export default React.memo(DataLabel);

